<template>
  <div v-if="loading">
    <v-skeleton-loader height="50px" class="mx-4" />
    <v-skeleton-loader type="chip" />
    <v-skeleton-loader type="text" />
    <v-skeleton-loader type="chip" />
    <v-skeleton-loader type="button" />
  </div>

  <v-form v-else>
    <div class="d-flex items-center mb-6">
      <p v-if="!updateFlow" class="d-flex align-center">
        {{ t("users.addEdit.createDescription") }}
      </p>

      <v-btn
        v-if="updateFlow"
        :loading="submitLoading"
        :disabled="v$.$invalid || !changed"
        class="ms-auto"
        @click="updateUser"
      >
        {{ t("users.addEdit.saveButton") }}
      </v-btn>
      <v-btn
        v-else
        :loading="submitLoading"
        :disabled="v$.$invalid"
        class="ms-auto"
        @click="addUser"
      >
        {{ t("users.addEdit.createButton") }}
      </v-btn>
    </div>
    <v-text-field
      v-model="userForm.email"
      type="email"
      :label="t('users.addEdit.emailLabel')"
      :hint="t('users.addEdit.emailHint')"
      :disabled="!!currentUserId"
      :error-messages="validationErrors(v$.email.$errors)"
    />
    <v-text-field
      v-model="userForm.firstName"
      type="text"
      :label="t('users.addEdit.firstNameLabel')"
      clearable
    />
    <v-text-field
      v-model="userForm.lastName"
      type="text"
      :label="t('users.addEdit.lastNameLabel')"
      clearable
    />
    <h3 class="text-primary text-uppercase mt-5">
      {{ t("users.addEdit.permissionsTitle") }}
    </h3>
    <v-checkbox
      v-model="userForm.admin"
      :label="t('users.addEdit.adminLabel')"
      persistent-hint
      :hint="t('users.addEdit.adminHint')"
      inset
      color="primary"
    />
    <div class="d-flex my-5 flex-wrap">
      <v-checkbox
        v-model="userForm.allSites"
        :label="t('users.addEdit.allSitesLabel')"
        :disabled="userForm.admin"
        inset
        color="primary"
        class="me-5"
        min-width="200px"
      />
      <v-autocomplete
        v-if="!userForm.admin && !userForm.allSites"
        v-model="userForm.sites"
        :label="t('users.addEdit.selectSitesLabel')"
        persistent-hint
        :hint="t('users.addEdit.selectSitesHint')"
        :error-messages="v$.sites.$invalid && v$.sites.$dirty ? t('users.addEdit.sitesError') : []"
        multiple
        chips
        clearable
        closable-chips
        :items="sites"
        item-title="siteName"
        item-value="siteId"
        :loading="!sites.length"
      />
    </div>
    <div v-if="updateFlow">
      <v-row>
        <v-col v-if="authStore.hasCompanyScoped(Permission.User_ResetPassword)">
          <h3 class="text-primary text-uppercase">
            {{ t("users.addEdit.resetPasswordTitle") }}
          </h3>
          <p class="my-4">
            {{ t("users.addEdit.resetPasswordDescription") }}
          </p>
          <v-btn-nd
            :loading="resetPasswordLoading"
            :disable="submitLoading"
            @click="resetUserPassword"
          >
            {{ t("users.addEdit.resetPasswordButton") }}
          </v-btn-nd>
        </v-col>
        <v-col>
          <h3 class="text-error text-uppercase">
            {{ t("users.addEdit.removeUserTitle") }}
          </h3>
          <p class="my-4">
            {{ t("users.addEdit.removeUserDescription") }}
          </p>
          <v-btn :loading="submitLoading" class="bg-error" @click="dissociateUser">
            {{ t("users.addEdit.removeUserButton") }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<script setup lang="ts">
import { ListSites200ResponseDataItemsInner } from "@tableyeti/merchant-service/api.ts";
import useVuelidate, { ErrorObject } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { AxiosError } from "axios";
import * as _ from "lodash-es";
import { onMounted, computed, onBeforeMount, inject, watch, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { User, UserInvite } from "@/api/merchant-service/users";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { API } from "@/plugins/api";
import { useApplicationStore } from "@/store/application";
import { Permission, Role, useAuthorizationStore } from "@/store/authorization";

const router = useRouter();
const route = useRoute();
const appStore = useApplicationStore();
const authStore = useAuthorizationStore();
const legacyApi = inject<API>("api");
const { t } = useI18n();

const { api, currentMerchantId } = useYetipayApi();
const sites = ref<ListSites200ResponseDataItemsInner[]>([]);
onMounted(
  async () =>
    (sites.value = await api
      .listSites(currentMerchantId)
      .then((response) => response.data.data.items)),
);

const currentUserId = ref<string | undefined>(undefined);
onBeforeMount(async () => {
  currentUserId.value = route.params.userId as string | undefined;
  if (!currentUserId.value) {
    loading.value = false;
  }
});
const updateFlow = computed(() => !!currentUserId.value);

interface UserUX {
  email: string;
  firstName?: string;
  lastName?: string;
  admin: boolean;
  allSites: boolean;
  sites: string[];
}
const fetchedUser = ref<UserUX | undefined>(undefined);
const userForm = ref<UserUX>({
  email: "",
  firstName: undefined,
  lastName: undefined,
  admin: false,
  allSites: true,
  sites: [],
});
const loading = ref(true);
watch(currentUserId, async () => {
  if (!currentUserId.value) {
    return;
  }
  const response = await legacyApi!.user.getUser(currentUserId.value);
  fetchedUser.value = {
    email: response.email,
    firstName: response.firstName,
    lastName: response.lastName,
    admin: response.companyRoles.includes(Role.CompanyOwner),
    allSites: response.siteRoles.some((r) => r.siteId === "*"),
    sites: response.siteRoles.filter((r) => r.siteId !== "*").map((r) => r.siteId),
  };
  userForm.value = _.cloneDeep(fetchedUser.value);
  loading.value = false;
});

const v$ = useVuelidate(
  {
    email: { required, email, $autoDirty: true },
    sites: {
      valid: () =>
        userForm.value.admin || userForm.value.allSites || userForm.value.sites.length > 0,
      $autoDirty: true,
    },
  },
  userForm,
);
const changed = computed(
  () =>
    fetchedUser.value && !_.isEqual(userFormData(userForm.value), userFormData(fetchedUser.value)),
);
watch(
  () => userForm.value.admin,
  () => {
    if (userForm.value.admin) {
      userForm.value.allSites = true;
    }
  },
);

const submitLoading = ref(false);
const userFormData = (uxData: UserUX): UserInvite => ({
  email: uxData.email,
  firstName: _.isEmpty(uxData.firstName) ? undefined : uxData.firstName,
  lastName: _.isEmpty(uxData.lastName) ? undefined : uxData.lastName,
  companyRoles: uxData.admin ? [Role.CompanyOwner] : [],
  siteRoles: uxData.allSites
    ? [{ siteId: "*", role: Role.SiteManager }]
    : uxData.sites.map((siteId) => ({ siteId, role: Role.SiteManager })),
});
const addUser = async () => {
  try {
    submitLoading.value = true;
    await legacyApi!.user.inviteUser(userFormData(userForm.value));
    appStore.notifyUser({
      message: t("users.addEdit.successMessage", { action: "created" }),
      type: "success",
    });
    router.push("/users");
  } catch (e) {
    console.error(e);
    appStore.notifyUser({
      message: t("users.addEdit.errorMessage", { action: "create" }),
      type: "error",
    });
  } finally {
    submitLoading.value = false;
  }
};
const updateUser = async () => {
  try {
    submitLoading.value = true;
    const userToSubmit: User = {
      id: currentUserId.value!,
      ...userFormData(userForm.value),
    };
    await legacyApi!.user.updateUser(userToSubmit);
    appStore.notifyUser({
      message: t("users.addEdit.successMessage", { action: "updated" }),
      type: "success",
    });
    router.push("/users");
  } catch (e) {
    console.error(e);
    appStore.notifyUser({
      message: t("users.addEdit.errorMessage", { action: "update" }),
      type: "error",
    });
  } finally {
    submitLoading.value = false;
  }
};
const resetPasswordLoading = ref(false);
const resetUserPassword = async () => {
  try {
    resetPasswordLoading.value = true;
    await legacyApi!.user.resetUserPassword(currentUserId.value!);
    appStore.notifyUser({ message: t("users.addEdit.resetPasswordSuccess"), type: "success" });
  } catch (e) {
    console.error(e);
    appStore.notifyUser({ message: t("users.addEdit.resetPasswordError"), type: "error" });
  } finally {
    resetPasswordLoading.value = false;
  }
};
const dissociateUser = async () => {
  try {
    submitLoading.value = true;
    await legacyApi!.user.dissociateUser(currentUserId.value!);
    appStore.notifyUser({ message: t("users.addEdit.removeUserSuccess"), type: "success" });
    router.push("/users");
  } catch (e) {
    console.error(e);
    let snackMessage = t("users.addEdit.removeUserError");
    if (e instanceof AxiosError) {
      snackMessage = e.response?.data?.error || snackMessage;
    }
    appStore.notifyUser({ message: snackMessage, type: "error" });
  } finally {
    submitLoading.value = false;
  }
};

const validationErrors = (errors: ErrorObject[]) => errors.map((e) => e.$message.toString());
</script>
